import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type CollectionAdminItem,
  type CreateCollectionInput,
  isCollectionComplete,
  LOCALES,
  type Locale,
  SlugSchema,
} from '@bailafm/shared';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, GripVertical, Loader2, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  CollectionImageField,
  type PendingImage,
} from '@/components/collections/CollectionImageField';
import { CopyUrlButton } from '@/components/collections/CopyUrlButton';
import {
  type AdminEventSearchItem,
  ApiClientError,
  collectionsApi,
  eventsApi,
} from '@/lib/api';
import { slugify, slugifyInput } from '@/lib/crop-image';
import { LOCALE_LABELS } from '@/lib/public-urls';
import { cn } from '@/lib/utils';

// Diálogo de crear / editar colección. Si recibe `collection`, edita
// (PATCH con If-Match); si no, crea (POST). Las imágenes se suben tras
// crear/guardar (necesitan el id de la colección).

interface CollectionFormDialogProps {
  open: boolean;
  collection?: CollectionAdminItem;
  onOpenChange(open: boolean): void;
  /** Crea/actualiza y devuelve el item resultante (con id). */
  onSubmit(
    values: CreateCollectionInput,
    collection?: CollectionAdminItem,
  ): Promise<CollectionAdminItem>;
  /** Se llama tras guardar + subir imágenes, para refrescar la lista. */
  onSaved?(): void;
}

interface FormErrors {
  title?: string;
  slug?: string;
  submit?: string;
}

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 10;

export function CollectionFormDialog({
  open,
  collection,
  onOpenChange,
  onSubmit,
  onSaved,
}: CollectionFormDialogProps) {
  const isEdit = Boolean(collection);

  const [activeTab, setActiveTab] = useState<Locale>('es');
  const [titles, setTitles] = useState<Record<Locale, string>>({ es: '', ca: '', en: '' });
  const [descriptions, setDescriptions] = useState<Record<Locale, string>>({
    es: '',
    ca: '',
    en: '',
  });
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const [pageImage, setPageImage] = useState<PendingImage | null>(null);
  const [thumbnailImage, setThumbnailImage] = useState<PendingImage | null>(null);

  const [selected, setSelected] = useState<AdminEventSearchItem[]>([]);
  const [hydrating, setHydrating] = useState(false);

  const [search, setSearch] = useState('');
  const [results, setResults] = useState<AdminEventSearchItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Resetea / hidrata el form al abrir.
  useEffect(() => {
    if (!open) return;
    setActiveTab('es');
    setTitles({
      es: collection?.label.es ?? '',
      ca: collection?.label.ca ?? '',
      en: collection?.label.en ?? '',
    });
    setDescriptions({
      es: collection?.description?.es ?? '',
      ca: collection?.description?.ca ?? '',
      en: collection?.description?.en ?? '',
    });
    setSlug(collection?.slug ?? '');
    setSlugTouched(Boolean(collection));
    setPageImage(null);
    setThumbnailImage(null);
    setSearch('');
    setResults([]);
    setSearchError(null);
    setSearchOpen(false);
    setErrors({});

    const refs = collection?.events ?? [];
    if (refs.length === 0) {
      setSelected([]);
      return;
    }
    setHydrating(true);
    const orderedIds = [...refs].sort((a, b) => a.position - b.position).map((e) => e.eventId);
    eventsApi
      .byIds(orderedIds)
      .then((items) => {
        const byId = new Map(items.map((i) => [i.id, i]));
        setSelected(
          orderedIds.map((id) => byId.get(id) ?? { id, title: `(evento ${id} no disponible)` }),
        );
      })
      .catch(() => {
        setSelected(orderedIds.map((id) => ({ id, title: `(evento ${id})` })));
      })
      .finally(() => setHydrating(false));
  }, [open, collection]);

  // Autogenera el slug desde el título ES mientras no se edite a mano
  // y solo en creación (en edición es inmutable).
  useEffect(() => {
    if (isEdit || slugTouched) return;
    setSlug(slugify(titles.es));
  }, [titles.es, isEdit, slugTouched]);

  // Búsqueda de eventos con debounce.
  const requestSeqRef = useRef(0);
  useEffect(() => {
    if (!open) return;
    const term = search.trim();
    if (term.length === 0) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }
    const seq = ++requestSeqRef.current;
    setSearching(true);
    setSearchError(null);
    const handle = window.setTimeout(() => {
      eventsApi
        .search({ q: term, page: 1, pageSize: SEARCH_PAGE_SIZE })
        .then((res) => {
          if (seq !== requestSeqRef.current) return;
          setResults(res.items);
        })
        .catch((err) => {
          if (seq !== requestSeqRef.current) return;
          setResults([]);
          setSearchError(
            err instanceof ApiClientError ? err.message : 'No se pudo buscar eventos.',
          );
        })
        .finally(() => {
          if (seq !== requestSeqRef.current) return;
          setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [search, open]);

  useEffect(() => {
    if (!searchOpen) return;
    function handle(event: PointerEvent) {
      const root = searchContainerRef.current;
      if (root && !root.contains(event.target as Node)) setSearchOpen(false);
    }
    document.addEventListener('pointerdown', handle);
    return () => document.removeEventListener('pointerdown', handle);
  }, [searchOpen]);

  const selectedIds = useMemo(() => new Set(selected.map((e) => e.id)), [selected]);

  const handleAdd = useCallback((event: AdminEventSearchItem) => {
    setSelected((prev) => (prev.some((e) => e.id === event.id) ? prev : [...prev, event]));
    setSearch('');
    setResults([]);
    setSearchOpen(false);
  }, []);

  const handleRemove = useCallback((id: string) => {
    setSelected((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSelected((prev) => {
      const oldIndex = prev.findIndex((e) => e.id === active.id);
      const newIndex = prev.findIndex((e) => e.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  // Label/description trilingües desde el estado (solo locales con texto).
  const buildTrilingual = (src: Record<Locale, string>) => {
    const out: { es?: string; ca?: string; en?: string } = {};
    for (const l of LOCALES) {
      const v = src[l].trim();
      if (v) out[l] = v;
    }
    return out;
  };

  const label = buildTrilingual(titles);
  const description = buildTrilingual(descriptions);
  // Tick global: los 3 idiomas con título y descripción.
  const complete = isCollectionComplete({ label, description });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    const candidate: CreateCollectionInput = {
      // Normaliza el guion final residual (p. ej. "rock-") antes de
      // validar/enviar; en edición el slug es inmutable y el PATCH lo ignora.
      slug: slugify(slug),
      label,
      description,
      eventIds: selected.map((event) => event.id),
    };

    // Título ES obligatorio siempre. El slug se valida (kebab-case)
    // solo al crear: en edición es inmutable y el PATCH lo ignora, así
    // que no debe bloquear el guardado (incluidas colecciones antiguas
    // sin slug).
    if (!titles.es.trim()) {
      setErrors({ title: 'El título en español es obligatorio.' });
      setActiveTab('es');
      setSubmitting(false);
      return;
    }
    if (!isEdit && !SlugSchema.safeParse(candidate.slug).success) {
      setErrors({ slug: 'El slug debe ser kebab-case: minúsculas, números y guiones.' });
      setSubmitting(false);
      return;
    }

    try {
      const saved = await onSubmit(candidate, collection);
      // Sube las imágenes pendientes ya con el id disponible.
      if (pageImage) {
        await collectionsApi.uploadImage(saved.id, 'page', pageImage.file, pageImage.crop);
      }
      if (thumbnailImage) {
        await collectionsApi.uploadImage(
          saved.id,
          'thumbnail',
          thumbnailImage.file,
          thumbnailImage.crop,
        );
      }
      toast.success(isEdit ? 'Cambios guardados' : 'Colección creada');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? formatApiError(err)
          : 'No se pudo guardar la colección.';
      setErrors({ submit: message });
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  const preview = useMemo(() => computePreview(selected), [selected]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? 'Editar colección' : 'Crear colección'}
            {complete && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700"
                title="Las tres traducciones están completas"
              >
                <Check className="size-3.5" aria-hidden /> Completa
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            El título en español es obligatorio; el slug se genera de él y no se
            puede cambiar una vez guardado. Las traducciones se editan por
            pestañas.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Slug */}
          <section className="space-y-1.5">
            <Label htmlFor="collection-slug">Slug</Label>
            <div className="flex items-center gap-1">
              <Input
                id="collection-slug"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  // Saneo tolerante mientras se teclea (conserva el guion
                  // en curso); la normalización final va en onBlur.
                  setSlug(slugifyInput(e.target.value));
                }}
                onBlur={() => setSlug((s) => slugify(s))}
                placeholder="festivales-verano"
                autoComplete="off"
                disabled={isEdit}
                className="flex-1"
              />
              <CopyUrlButton slug={slug} label={label} />
            </div>
            {isEdit && (
              <p className="text-xs text-muted-foreground">
                El slug es inmutable una vez creada la colección.
              </p>
            )}
            {errors.slug && <p className="text-sm text-destructive">{errors.slug}</p>}
          </section>

          {/* Pestañas por idioma */}
          <section className="space-y-3">
            <div className="flex gap-1 rounded-md bg-muted p-1">
              {LOCALES.map((l) => {
                const localeComplete =
                  titles[l].trim().length > 0 && descriptions[l].trim().length > 0;
                return (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setActiveTab(l)}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition-colors',
                      activeTab === l
                        ? 'bg-background shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {LOCALE_LABELS[l]}
                    {localeComplete && <Check className="size-3.5 text-emerald-600" aria-hidden />}
                  </button>
                );
              })}
            </div>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor={`title-${activeTab}`}>
                  Título {activeTab === 'es' && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id={`title-${activeTab}`}
                  value={titles[activeTab]}
                  onChange={(e) =>
                    setTitles((prev) => ({ ...prev, [activeTab]: e.target.value }))
                  }
                  placeholder="Festivales de verano"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`desc-${activeTab}`}>Descripción</Label>
                <Textarea
                  id={`desc-${activeTab}`}
                  value={descriptions[activeTab]}
                  onChange={(e) =>
                    setDescriptions((prev) => ({ ...prev, [activeTab]: e.target.value }))
                  }
                  placeholder="Describe la colección…"
                  rows={4}
                />
              </div>
            </div>
            {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
          </section>

          {/* Imágenes */}
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CollectionImageField
              title="Imagen de la página (3:4)"
              hint="Recomendado ≥ 900×1200 · vertical"
              aspect={3 / 4}
              existingUrl={collection?.images.page?.url}
              onChange={setPageImage}
            />
            <CollectionImageField
              title="Miniatura de la home (2:1)"
              hint="Recomendado ≥ 1200×600 · panorámica"
              aspect={2 / 1}
              existingUrl={collection?.images.thumbnail?.url}
              onChange={setThumbnailImage}
            />
          </section>

          {/* Buscar eventos */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Buscar eventos
            </h3>
            <div ref={searchContainerRef} className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => {
                  if (search.trim().length > 0) setSearchOpen(true);
                }}
                placeholder="Empieza a escribir para buscar…"
                className="pl-9"
                autoComplete="off"
              />
              {searching && (
                <Loader2
                  className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground"
                  aria-hidden
                />
              )}
              {searchOpen && (results.length > 0 || searching || searchError) && (
                <div
                  role="listbox"
                  className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
                >
                  {searchError ? (
                    <p className="px-2 py-1.5 text-sm text-destructive">{searchError}</p>
                  ) : results.length === 0 ? (
                    <p className="px-2 py-1.5 text-sm text-muted-foreground">Buscando…</p>
                  ) : (
                    <ul className="space-y-1">
                      {results.map((event) => {
                        const already = selectedIds.has(event.id);
                        return (
                          <li key={event.id}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={already}
                              onClick={() => handleAdd(event)}
                              disabled={already}
                              className={cn(
                                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                                already
                                  ? 'cursor-default opacity-50'
                                  : 'hover:bg-accent hover:text-accent-foreground',
                              )}
                            >
                              <EventThumb url={event.imageUrl} />
                              <div className="min-w-0 flex-1">
                                <p className="truncate font-medium">{event.title}</p>
                                <p className="truncate text-xs text-muted-foreground">
                                  {summarize(event)}
                                </p>
                              </div>
                              {already ? (
                                <span className="text-xs text-muted-foreground">añadido</span>
                              ) : (
                                <Plus className="size-4 text-muted-foreground" aria-hidden />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="space-y-3">
            <header className="flex items-baseline justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Eventos en la colección
              </h3>
              <span className="text-xs text-muted-foreground">
                {selected.length} {selected.length === 1 ? 'evento' : 'eventos'} · {preview}
              </span>
            </header>
            {hydrating ? (
              <p className="rounded-md border bg-card p-4 text-sm text-muted-foreground">
                Cargando eventos…
              </p>
            ) : selected.length === 0 ? (
              <p className="rounded-md border border-dashed bg-card p-4 text-center text-sm text-muted-foreground">
                Aún no has añadido ningún evento.
              </p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={selected.map((e) => e.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="space-y-2">
                    {selected.map((event) => (
                      <SortableEventRow
                        key={event.id}
                        event={event}
                        onRemove={() => handleRemove(event.id)}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </section>

          {errors.submit && <p className="text-sm text-destructive">{errors.submit}</p>}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear colección'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface SortableEventRowProps {
  event: AdminEventSearchItem;
  onRemove(): void;
}

function SortableEventRow({ event, onRemove }: SortableEventRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: event.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li ref={setNodeRef} style={style} className={cn(isDragging && 'z-10')}>
      <article
        className={cn(
          'flex items-center gap-3 rounded-lg border bg-card p-2',
          isDragging && 'shadow-lg ring-2 ring-foreground/20',
        )}
      >
        <button
          type="button"
          aria-label="Arrastra para reordenar"
          className="flex size-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
        <EventThumb url={event.imageUrl} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{event.title}</p>
          <p className="truncate text-xs text-muted-foreground">{summarize(event)}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Quitar evento"
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive"
        >
          <X className="size-4" aria-hidden />
        </Button>
      </article>
    </li>
  );
}

function EventThumb({ url }: { url: string | undefined }) {
  if (!url) return <div aria-hidden className="size-10 shrink-0 rounded bg-muted" />;
  return <img src={url} alt="" className="size-10 shrink-0 rounded object-cover" loading="lazy" />;
}

function summarize(event: AdminEventSearchItem): string {
  const parts: string[] = [];
  if (event.city) parts.push(event.city);
  if (event.venue) parts.push(event.venue);
  if (event.startAt) parts.push(formatDate(event.startAt));
  return parts.length === 0 ? '—' : parts.join(' · ');
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function computePreview(events: AdminEventSearchItem[]): string {
  if (events.length === 0) return 'sin datos calculados';
  const prices = events.map((e) => e.minPrice).filter((p): p is number => typeof p === 'number');
  const starts = events.map((e) => e.startAt).filter(Boolean) as string[];
  const ends = events.map((e) => e.endAt ?? e.startAt).filter(Boolean) as string[];
  const minPrice =
    prices.length === 0
      ? null
      : new Intl.NumberFormat('es-ES', {
          style: 'currency',
          currency: 'EUR',
          maximumFractionDigits: 0,
        }).format(Math.min(...prices));
  const from = starts.length === 0 ? null : formatDate(starts.reduce((m, c) => (c < m ? c : m)));
  const to = ends.length === 0 ? null : formatDate(ends.reduce((m, c) => (c > m ? c : m)));
  const dateRange = from && to ? (from === to ? from : `${from} → ${to}`) : 'sin fechas';
  const priceLabel = minPrice ? `desde ${minPrice}` : 'sin precio';
  return `${dateRange} · ${priceLabel}`;
}

function formatApiError(err: ApiClientError): string {
  if (err.code === 'etag_mismatch') {
    return 'Otra persona modificó esta colección. Recarga la lista y vuelve a intentarlo.';
  }
  if (err.code === 'collection_slug_taken') {
    return 'Ya existe una colección con ese slug. Elige otro.';
  }
  if (err.code === 'validation_error') {
    return `Datos no válidos: ${err.message}`;
  }
  return err.message || 'No se pudo guardar la colección.';
}
